/* eslint-disable no-eval */
// Constants
const INNINGS_PER_BASEBALL_GAME = 9


// Functions

export const sum = (numbers) => (
  numbers.reduce((a, b) => Number(a) + Number(b), 0)
)

export const average = (numbers) => (
  sum(numbers) / numbers.length
)

export const averageToOneDecimal = (numbers) => (
  average(numbers).toFixed(1)
)

export const averageToTwoDecimals = (numbers) => (
  average(numbers).toFixed(2)
)

export const averageToThreeDecimals = (numbers) => (
  average(numbers).toFixed(3)
)

export const inningsPitched = (inningFractions) => {
  const split = inningFractions.map(mixedNumber => mixedNumber.split(" "))

  const wholeInnings = split.map(s => s[0])
  const sumOfWholeInnings = sum(wholeInnings)

  const fractions = split.map(s => s[1]).filter(fraction => fraction)
  const numerators = fractions.map(s => s.split("/")[0])
  const sumOfNumerators = sum(numerators)
  const inningsFromFractions = Math.floor(sumOfNumerators / 3)

  const totalWholeInnings = sumOfWholeInnings + inningsFromFractions
  const partialInnings = sumOfNumerators % 3

  return partialInnings > 0 ? `${totalWholeInnings} ${partialInnings}/3` : `${totalWholeInnings}`
}

export const battingAverage = (_, fieldSets) => {
  const atBatsEachGame = fieldStatValues("At Bats", fieldSets)
  const hitsEachGame = fieldStatValues("Hits", fieldSets)

  return (sum(hitsEachGame) / sum(atBatsEachGame)).toFixed(3).replace(/^0/, "")
}

export const sluggingPercentage = (_, fieldSets) => {
  const atBats = sum(fieldStatValues("At Bats", fieldSets))
  const hits = sum(fieldStatValues("Hits", fieldSets))
  const doubles = sum(fieldStatValues("Doubles", fieldSets))
  const triples = sum(fieldStatValues("Triples", fieldSets))
  const homeRuns = sum(fieldStatValues("Home Runs", fieldSets))
  const singles = hits - doubles - triples - homeRuns

  const basesFromHits = (4 * homeRuns) + (3 * triples) + (2 * doubles) + singles

  return (basesFromHits / atBats).toFixed(3).replace(/^0/, "")
}

export const ERA = (earnedRunAverages, fieldSets) => {
  const inningsPitchedEachGame = fieldStatValues("Innings Pitched", fieldSets).map(ip => (
    sum(ip.split(" ").map(x => eval(x)))
  ))

  const earnedRunsAllowedEachGame = earnedRunAverages.map((era, index) => (
    era * inningsPitchedEachGame[index] / INNINGS_PER_BASEBALL_GAME
  ))

  const totalEarnedRunsAllowed = sum(earnedRunsAllowedEachGame)
  const totalInningsPitched = sum(inningsPitchedEachGame)

  return (totalEarnedRunsAllowed * INNINGS_PER_BASEBALL_GAME / totalInningsPitched).toFixed(2)
}

export const ratio = (ratios) => {
  const split = ratios.map(r => r.split("/"))
  const numerators = split.map(s => s[0])
  const denominators = split.map(s => s[1])

  return `${sum(numerators)}/${sum(denominators)}`
}

export const minimum = (values) => values.reduce(lesserOf)

export const maximum = (values) => values.reduce(greaterOf)

export const minMax = (values) => {
  const min = minimum(values)
  const max = maximum(values)

  return min === max ? min : `${min}-${max}`
}

export const averageTime = (times) => {
  const timesInSeconds = times.map(t => timeToSeconds(t))
  const averageInSeconds = average(timesInSeconds)

  return secondsToTime(averageInSeconds)
}

export const totalTime = (times) => {
  const timesInSeconds = times.map(t => timeToSeconds(t))
  const totalSeconds = sum(timesInSeconds)

  return secondsToTime(totalSeconds).replace(/\.\d*$/i, "")
}

export const fieldGoalDistances = (distanceLists) => (
  minMax(distanceLists.map(l => l.split(", ")).reduce((a, b) => a.concat(b)))
)

/* eslint-disable no-eval */
export const averageFeetAndInches = (measurements) => {
  const inInches = measurements.map(m => {
    const [feet, inches, fraction] = m.split(" ").map(x => eval(x.replace(/['"]/g, "")))

    return feet * 12 + inches + (fraction || 0)
  })

  const averageInches = average(inInches)

  return inchesToMeasurement(averageInches)
}

export const volleyballPlayedAndStarted = (values) => {
  const split = values.map(v => v.split("-"))

  const setsPlayed = split.map(s => s[0])
  const matchesPlayed = split.map(s => s[1])
  const matchesStarted = split.map(s => s[2])

  return `${sum(setsPlayed)}-${sum(matchesPlayed)}-${sum(matchesStarted)}`
}

export const strokeSwam = (strokes) => {
  const unique = Array.from(new Set(strokes))

  return unique.map(stroke => stroke.slice(0, 2)).join(", ")
}

export const tennisPositions = (positions) => {
  const unique = Array.from(new Set(positions))
  return unique.sort((a, b) => a < b ? -1 : 1).join(", ")
}

export const tennisSetScore = (scores) => {
  const split = scores.map(r => r.split(":"))
  const won = split.map(s => s[0])
  const lost = split.map(s => s[1])

  return `${sum(won)}:${sum(lost)}`
}

export const yardsPerCarry = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Rushing Attempts", fieldSets))
  const totalYardage = sum(fieldStatValues("Net Rushing Yards", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const yardsPerReceptions = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Number of Receptions", fieldSets))
  const totalYardage = sum(fieldStatValues("Receiving Yards", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const qbYardsPerCarry = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Rushing Attempts", fieldSets))
  const totalYardage = sum(fieldStatValues("Net Rushing Yards", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const completionPercentage = (_, fieldSets) => {
  const attempts = sum(fieldStatValues("Passing Attempts", fieldSets))
  const completions = sum(fieldStatValues("Completions", fieldSets))

  return (attempts / completions).toFixed(2)
}

export const yardsPerPunt = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Number of Punts", fieldSets))
  const totalYardage = sum(fieldStatValues("Punting Yardage", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const yardsPerKickoff = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Number of Kickoffs", fieldSets))
  const totalYardage = sum(fieldStatValues("Kickoff Yardage", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const yardsPerPuntReturn = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Number of Punt Returns", fieldSets))
  const totalYardage = sum(fieldStatValues("Punt Return Yardage", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const yardsPerKickoffReturn = (_, fieldSets) => {
  const attemptsCount = sum(fieldStatValues("Number of Kickoff Returns", fieldSets))
  const totalYardage = sum(fieldStatValues("Kickoff Return Yardage", fieldSets))

  return yardsPerAttempt(attemptsCount, totalYardage)
}

export const killsPerSet = (_, fieldSets) => {
  const sp_mp_ms = fieldStatValues("Sets Played - Matches Played - Matches Started", fieldSets)

  const setsPlayed = sum(sp_mp_ms.map(x => x.split("-")[0]))
  const kills = sum(fieldStatValues("Kills", fieldSets))

  return (kills / setsPlayed).toFixed(1)
}

export const assistsPerSet = (_, fieldSets) => {
  const sp_mp_ms = fieldStatValues("Sets Played - Matches Played - Matches Started", fieldSets)

  const setsPlayed = sum(sp_mp_ms.map(x => x.split("-")[0]))
  const assists = sum(fieldStatValues("Assists", fieldSets))

  return (assists / setsPlayed).toFixed(1)
}

export const acesPerSet = (_, fieldSets) => {
  const sp_mp_ms = fieldStatValues("Sets Played - Matches Played - Matches Started", fieldSets)

  const setsPlayed = sum(sp_mp_ms.map(x => x.split("-")[0]))
  const aces = sum(fieldStatValues("Service Aces", fieldSets))

  return (aces / setsPlayed).toFixed(1)
}

export const digsPerSet = (_, fieldSets) => {
  const sp_mp_ms = fieldStatValues("Sets Played - Matches Played - Matches Started", fieldSets)

  const setsPlayed = sum(sp_mp_ms.map(x => x.split("-")[0]))
  const digs = sum(fieldStatValues("Digs", fieldSets))

  return (digs / setsPlayed).toFixed(1)
}

export const blocksPerSet = (_, fieldSets) => {
  const sp_mp_ms = fieldStatValues("Sets Played - Matches Played - Matches Started", fieldSets)

  const setsPlayed = sum(sp_mp_ms.map(x => x.split("-")[0]))
  const blocks = sum(fieldStatValues("Total Blocks", fieldSets))

  return (blocks / setsPlayed).toFixed(1)
}

export const pointsPerSet = (_, fieldSets) => {
  const sp_mp_ms = fieldStatValues("Sets Played - Matches Played - Matches Started", fieldSets)

  const setsPlayed = sum(sp_mp_ms.map(x => x.split("-")[0]))
  const points = sum(fieldStatValues("Points", fieldSets))

  return (points / setsPlayed).toFixed(1)
}

export const hittingEfficiency = (_, fieldSets) => {
  const kills = sum(fieldStatValues("Kills", fieldSets))
  const errors = sum(fieldStatValues("Attack Errors", fieldSets))
  const attempts = sum(fieldStatValues("Attack Total Attempts", fieldSets))

  return ((kills - errors) / attempts).toFixed(3).replace(/^0/, "")
}

export const assistPercentage = (_, fieldSets) => {
  const assists = sum(fieldStatValues("Assists", fieldSets))
  const attempts = sum(fieldStatValues("Assist Total Attempts", fieldSets))

  return (assists / attempts).toFixed(3).replace(/^0/, "")
}

export const servicePercentage = (_, fieldSets) => {
  const attempts = sum(fieldStatValues("Service Total Attempts", fieldSets))
  const errors = sum(fieldStatValues("Service Errors", fieldSets))

  return ((attempts - errors) / attempts).toFixed(3).replace(/^0/, "")
}

export const receptionPercentage = (_, fieldSets) => {
  const attempts = sum(fieldStatValues("Reception Total Attempts", fieldSets))
  const errors = sum(fieldStatValues("Service Reception Errors", fieldSets))

  return ((attempts - errors) / attempts).toFixed(3).replace(/^0/, "")
}


// --- Helper functions ---
const fieldStats = (fieldName, fieldSets) => (
  fieldSets.map(fieldSet => fieldSet.find(field => field.name === fieldName))
)

const fieldStatValues = (fieldName, fieldSets) => (
  fieldStats(fieldName, fieldSets).map(stats => stats.value)
)

const yardsPerAttempt = (attemptsCount, totalYardage) => (
  (totalYardage / attemptsCount).toFixed(1)
)

const timeToSeconds = (time) => {
  const [minutes, seconds] = time.split(":")
  const secondsFromMinutes = Number(minutes) * 60

  return secondsFromMinutes + Number(seconds)
}

const secondsToTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60).toFixed(0).padStart(2, "0")
  const remainingSeconds = (totalSeconds % 60).toFixed(2).padStart(5, "0")

  return `${minutes}:${remainingSeconds}`
}

const decimalToFraction = (decimal) => {
  const len = decimal.toString().length - 2

  let denominator = Math.pow(10, len)
  let numerator = decimal * denominator
  const divisor = greatestCommonDenominator(numerator, denominator)

  numerator /= divisor
  denominator /= divisor

  return Math.floor(numerator) + '/' + Math.floor(denominator)
}

const greatestCommonDenominator = (a, b) => {
  if (b < 0.0000001) return a

  return greatestCommonDenominator(b, Math.floor(a % b))
}

const inchesToMeasurement = (inches) => {
  const feet = Math.floor(inches / 12)
  const remainingInches = Math.floor(inches % 12)
  const remainingFractionalInches = (inches % 12) - remainingInches

  return !remainingFractionalInches
  ? `${feet}' ${remainingInches}"`
  : `${feet}' ${remainingInches} ${decimalToFraction(remainingFractionalInches)}"`
}

const lesserComparisonFunction = (...values) => (
  values.map(v => parseInt(v, 10)).every(Boolean)
  ? (a, b) => parseInt(a, 10) < parseInt(b, 10) ? a : b
  : (a, b) => a < b ? a : b
)

const lesserOf = (a, b) => lesserComparisonFunction(a, b)(a, b)

const greaterComparisonFunction = (...values) => (
  values.map(v => parseInt(v, 10)).every(Boolean)
  ? (a, b) => parseInt(a, 10) > parseInt(b, 10) ? a : b
  : (a, b) => a > b ? a : b
)

const greaterOf = (a, b) => greaterComparisonFunction(a, b)(a, b)



// WEBPACK FOOTER //
// ./src/utils/StatAlgorithms.js